import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { FileUp, FileText, Book, Plus, Trash2 } from 'lucide-react';

interface ContentStepProps {
  onNext: (data: Record<string, any>) => void;
  data?: Record<string, any>;
  isLastStep?: boolean;
}

export const ContentStep: React.FC<ContentStepProps> = ({ onNext, data = {} }) => {
  const [activeTab, setActiveTab] = useState('faq');
  const [faqs, setFaqs] = useState(data.faqs || [
    { question: 'Como funciona o serviço?', answer: 'Nosso serviço funciona integrando WhatsApp com IA para automatizar atendimentos.' },
    { question: 'Quais os horários de atendimento?', answer: 'Nosso atendimento funciona 24/7 via assistente virtual e em horário comercial com atendentes humanos.' }
  ]);
  const [aboutText, setAboutText] = useState(data.about_text || '');
  const [uploadedFiles, setUploadedFiles] = useState(data.uploaded_files || [
    { name: 'Catálogo de Produtos.pdf', size: '2.4 MB', uploaded: new Date().toISOString() },
    { name: 'Manual de Serviços.docx', size: '1.8 MB', uploaded: new Date().toISOString() }
  ]);

  const handleAddFaq = () => {
    setFaqs([...faqs, { question: '', answer: '' }]);
  };

  const handleRemoveFaq = (index: number) => {
    const newFaqs = [...faqs];
    newFaqs.splice(index, 1);
    setFaqs(newFaqs);
  };

  const handleFaqChange = (index: number, field: 'question' | 'answer', value: string) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  const handleUploadFile = () => {
    // Simulação de upload - em produção seria integrado com um serviço de upload
    const mockFile = {
      name: `Documento-${Math.floor(Math.random() * 1000)}.pdf`,
      size: `${(Math.random() * 5).toFixed(1)} MB`,
      uploaded: new Date().toISOString()
    };
    setUploadedFiles([...uploadedFiles, mockFile]);
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  };

  const handleContinue = () => {
    onNext({
      faqs,
      about_text: aboutText,
      uploaded_files: uploadedFiles,
      configured: true
    });
  };

  return (
    <Card className="border-none shadow-none">
      <CardHeader>
        <CardTitle>Conteúdo do Assistente</CardTitle>
        <CardDescription>
          Configure o conteúdo que seu assistente utilizará para responder aos clientes
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <Book className="h-12 w-12 text-primary" />
          <div>
            <h3 className="font-medium text-lg">Base de Conhecimento</h3>
            <p className="text-sm text-muted-foreground">
              Forneça informações para o assistente responder às dúvidas dos clientes com precisão.
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="faq">Perguntas Frequentes</TabsTrigger>
            <TabsTrigger value="about">Sobre a Empresa</TabsTrigger>
            <TabsTrigger value="documents">Documentos</TabsTrigger>
          </TabsList>
          
          <TabsContent value="faq" className="space-y-4 pt-4">
            {faqs.map((faq, index) => (
              <div key={index} className="space-y-3 p-4 border rounded-md">
                <div className="flex justify-between items-start">
                  <Label htmlFor={`question-${index}`}>Pergunta {index + 1}</Label>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleRemoveFaq(index)}
                    className="h-8 w-8 text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                <Input
                  id={`question-${index}`}
                  value={faq.question}
                  onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                  placeholder="Digite a pergunta"
                />

                <Label htmlFor={`answer-${index}`}>Resposta</Label>
                <Textarea
                  id={`answer-${index}`}
                  value={faq.answer}
                  onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                  placeholder="Digite a resposta"
                  rows={3}
                />
              </div>
            ))}

            <Button 
              variant="outline" 
              onClick={handleAddFaq} 
              className="w-full"
              type="button"
            >
              <Plus className="h-4 w-4 mr-2" /> Adicionar Pergunta
            </Button>
          </TabsContent>
          
          <TabsContent value="about" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="about-text">Informações sobre a empresa</Label>
              <Textarea
                id="about-text"
                value={aboutText}
                onChange={(e) => setAboutText(e.target.value)}
                placeholder="Descreva sua empresa, produtos, serviços, missão, visão e valores..."
                rows={8}
              />
              <p className="text-sm text-muted-foreground">
                Estas informações ajudarão o assistente a entender melhor sua empresa e prestar um melhor atendimento.
              </p>
            </div>
          </TabsContent>
          
          <TabsContent value="documents" className="space-y-4 pt-4">
            <div className="space-y-4">
              <div className="p-4 border border-dashed rounded-md text-center hover:bg-accent/50 cursor-pointer" onClick={handleUploadFile}>
                <FileUp className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium">Clique para fazer upload de documentos</p>
                <p className="text-xs text-muted-foreground">Suporta PDF, DOCX, TXT, XLSX (máx. 10MB)</p>
              </div>

              {uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  <Label>Documentos Carregados</Label>
                  <div className="space-y-2">
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded-md">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{file.name}</p>
                            <p className="text-xs text-muted-foreground">{file.size}</p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleRemoveFile(index)}
                          className="h-8 w-8 text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={() => window.history.back()}>
          Voltar
        </Button>
        <Button onClick={handleContinue}>
          Continuar
        </Button>
      </CardFooter>
    </Card>
  );
}; 